import {
    ensureCsrfToken,
    resetCsrfToken,
} from '../utilities/csrf.js';

const MAX_SEND_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const POST_TIMEOUT_MS = 10000;
const CLIENT_ID_STORAGE_KEY = 'ga_client_id';
const SESSION_ID_STORAGE_KEY = 'ga_session_id';
const CONSENT_COOKIE = 'cookie_consent';

const hasValue = (value) => value !== null && value !== undefined && value !== '';
const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value, key);
const isRetryableStatus = (status) => status === 429 || status >= 500;

const createObservationId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }

    return `${Date.now()}.${Math.random().toString(36).slice(2)}`;
};

const dispatchWindowEvent = (name, detail) => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }

    if (typeof window.CustomEvent !== 'function') {
        return;
    }

    window.dispatchEvent(new window.CustomEvent(name, {detail}));
};

const isConsentRejected = () => {
    const cookieHeader = typeof document !== 'undefined' && typeof document.cookie === 'string'
        ? document.cookie
        : '';

    return cookieHeader.split(';').some((cookie) => cookie.trim() === `${CONSENT_COOKIE}=rejected`);
};

export default function analytics() {
    const metaTag = document.querySelector('meta[name="ga-measurement-id"]');

    if (!metaTag) {
        console.error('Meta-тег GA_MEASUREMENT_ID отсутствует.');
        return;
    }

    const measurementId = metaTag.getAttribute('content');

    if (!measurementId) {
        console.error('GA_MEASUREMENT_ID пустой.');
        return;
    }

    if (typeof gtag === 'undefined') {
        console.warn('gtag не инициализирован');
        return;
    }

    let observationId = createObservationId();
    const resolved = {
        clientId: null,
        sessionId: null,
        clientReady: false,
        sessionReady: false,
        fallbackFired: false,
        sentClientId: false,
        sentSessionId: false,
        transportRequested: false,
        sending: false,
        consentRejected: isConsentRejected(),
        generation: 0,
        fallbackTimer: null,
        retryTimers: new Set(),
        attemptsByPayload: new Map(),
        terminalPayloads: new Set(),
    };

    const storeStorageValue = (key, value) => {
        if (typeof window === 'undefined' || !window.sessionStorage) {
            return;
        }

        try {
            window.sessionStorage.setItem(key, String(value));
        } catch (error) {
            console.error('Ошибка сохранения GA идентификатора:', error);
        }
    };

    const clearStoredIdentifiers = () => {
        try {
            window.sessionStorage?.removeItem?.(CLIENT_ID_STORAGE_KEY);
            window.sessionStorage?.removeItem?.(SESSION_ID_STORAGE_KEY);
        } catch (error) {
            console.error('Ошибка очистки GA идентификаторов:', error);
        }

        if (typeof window !== 'undefined') {
            window.GA_SESSION_ID = null;
        }
    };

    const publishContext = (source = 'gtag') => {
        const detail = {
            clientId: resolved.clientId,
            sessionId: resolved.sessionId,
            observationId,
            source,
        };

        if (typeof window !== 'undefined') {
            window.__vsebankiGaContext = detail;
        }

        dispatchWindowEvent('ga-context-ready', detail);
    };

    const clearRetryTimers = () => {
        resolved.retryTimers.forEach((timerId) => clearTimeout(timerId));
        resolved.retryTimers.clear();
    };

    const clearFallbackTimer = () => {
        if (resolved.fallbackTimer !== null) {
            clearTimeout(resolved.fallbackTimer);
            resolved.fallbackTimer = null;
        }
    };

    const resetObservationState = () => {
        clearRetryTimers();
        clearFallbackTimer();
        observationId = createObservationId();
        resolved.clientId = null;
        resolved.sessionId = null;
        resolved.clientReady = false;
        resolved.sessionReady = false;
        resolved.fallbackFired = false;
        resolved.sentClientId = false;
        resolved.sentSessionId = false;
        resolved.attemptsByPayload.clear();
        resolved.terminalPayloads.clear();
    };

    const handleConsentRejected = () => {
        resolved.consentRejected = true;
        resolved.generation += 1;
        resetObservationState();
        clearStoredIdentifiers();
        publishContext('consent_rejected');
    };

    const handleConsentAccepted = () => {
        if (!resolved.consentRejected) {
            return;
        }

        resolved.consentRejected = false;
        resolved.generation += 1;
        resetObservationState();
        publishContext('gtag');
        requestGtagValues();
        scheduleFallback();
    };

    const storeClientId = (clientId) => {
        if (resolved.consentRejected) {
            return;
        }

        resolved.clientId = clientId;
        resolved.sentClientId = false;
        storeStorageValue(CLIENT_ID_STORAGE_KEY, clientId);
        publishContext();
    };

    const storeSessionId = (sessionId) => {
        if (resolved.consentRejected) {
            return;
        }

        resolved.sessionId = sessionId;
        resolved.sentSessionId = false;
        if (typeof window !== 'undefined') {
            window.GA_SESSION_ID = sessionId;
        }
        storeStorageValue(SESSION_ID_STORAGE_KEY, sessionId);
        publishContext();
        dispatchWindowEvent('ga-session-id-ready', {
            sessionId,
            observationId,
            source: 'gtag',
        });
    };

    const postIdentifiers = (payload, token, signal) => fetch('/api/ga/store-client-id', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'X-CSRF-TOKEN': token,
        },
        body: JSON.stringify(payload),
        signal,
    });

    const createAbortError = () => {
        const error = new Error('Ожидание ответа GA превысило таймаут');
        error.name = 'AbortError';
        return error;
    };

    const readResponseBody = async (response, signal) => {
        if (!response || typeof response.json !== 'function') {
            return null;
        }

        const bodyPromise = Promise.resolve().then(() => response.json());

        if (!signal) {
            try {
                return await bodyPromise;
            } catch (error) {
                if (error?.name === 'AbortError') {
                    throw error;
                }

                return null;
            }
        }

        try {
            return await new Promise((resolve, reject) => {
                let settled = false;

                const cleanup = () => {
                    signal.removeEventListener('abort', onAbort);
                };

                const resolveOnce = (value) => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    cleanup();
                    resolve(value);
                };

                const rejectOnce = (error) => {
                    if (settled) {
                        return;
                    }

                    settled = true;
                    cleanup();
                    reject(error);
                };

                const onAbort = () => rejectOnce(createAbortError());

                if (signal.aborted) {
                    onAbort();
                    return;
                }

                signal.addEventListener('abort', onAbort, {once: true});
                bodyPromise.then(resolveOnce, rejectOnce);
            });
        } catch (error) {
            if (error?.name === 'AbortError') {
                throw error;
            }

            return null;
        }
    };

    const postWithTimeout = async (payload, token) => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), POST_TIMEOUT_MS);

        try {
            const response = await postIdentifiers(payload, token, controller.signal);
            const body = await readResponseBody(response, controller.signal);

            return {response, body};
        } finally {
            clearTimeout(timeoutId);
        }
    };

    const sendAnalyticsPayload = async (payload, label) => {
        try {
            let token = await ensureCsrfToken();

            if (!token) {
                console.error(`${label}: не удалось получить CSRF-токен`);
                return {delivered: false, retryable: true, reason: 'csrf_unavailable'};
            }

            let result = await postWithTimeout(payload, token);

            if (result.response.status === 419) {
                resetCsrfToken();
                token = await ensureCsrfToken();

                if (!token) {
                    console.error(`${label}: не удалось обновить CSRF-токен`);
                    return {delivered: false, retryable: false, reason: 'csrf_refresh_failed'};
                }

                result = await postWithTimeout(payload, token);
            }

            const {response, body} = result;

            if (!response.ok) {
                console.error(`${label}: неуспешный ответ ${response.status}`);
                return {
                    delivered: false,
                    retryable: isRetryableStatus(response.status),
                    reason: `http_${response.status}`,
                };
            }

            if (body?.status === 'skipped') {
                return {
                    delivered: false,
                    retryable: false,
                    reason: body.reason || 'skipped',
                };
            }

            const stored = body?.stored || {};
            const clientStored = !hasOwn(payload, 'client_id') || stored.client_id === true;
            const sessionStored = !hasOwn(payload, 'session_id') || stored.session_id === true;

            if (body?.status !== 'success' || !clientStored || !sessionStored) {
                console.error(`${label}: неожиданный контракт ответа`);
                return {delivered: false, retryable: false, reason: 'invalid_response'};
            }

            console.debug(`${label} сохранен`);
            return {delivered: true, retryable: false, reason: 'stored'};
        } catch (error) {
            const reason = error?.name === 'AbortError' ? 'timeout' : 'network_error';
            console.error(`${label}: ошибка отправки`, error);
            return {delivered: false, retryable: true, reason};
        }
    };

    const buildPayload = () => {
        const payload = {};

        if (hasValue(resolved.clientId) && !resolved.sentClientId) {
            payload.client_id = resolved.clientId;
        }

        if (hasValue(resolved.sessionId) && !resolved.sentSessionId) {
            payload.session_id = resolved.sessionId;
        }

        if (Object.keys(payload).length > 0) {
            payload.observation_id = observationId;
        }

        return payload;
    };

    const markSent = (payload) => {
        if (hasOwn(payload, 'client_id')) {
            resolved.sentClientId = true;
        }

        if (hasOwn(payload, 'session_id')) {
            resolved.sentSessionId = true;
        }
    };

    const scheduleRetry = () => {
        const timerId = setTimeout(() => {
            resolved.retryTimers.delete(timerId);
            void maybeSendIdentifiers();
        }, RETRY_DELAY_MS);

        resolved.retryTimers.add(timerId);
    };

    const maybeSendIdentifiers = async () => {
        if (resolved.consentRejected || !resolved.transportRequested) {
            return;
        }

        if (!resolved.clientReady || !resolved.sessionReady) {
            if (!resolved.fallbackFired) {
                return;
            }
        }

        if (resolved.sending) {
            return;
        }

        const payload = buildPayload();

        if (Object.keys(payload).length === 0) {
            const nothingToSend = !hasValue(resolved.clientId) && !hasValue(resolved.sessionId);

            if (resolved.clientReady && resolved.sessionReady && nothingToSend) {
                console.warn('Client ID и Session ID отсутствуют');
            }

            return;
        }

        const payloadKey = JSON.stringify(payload);
        if (resolved.terminalPayloads.has(payloadKey)) {
            return;
        }

        const attempts = resolved.attemptsByPayload.get(payloadKey) || 0;

        if (attempts >= MAX_SEND_ATTEMPTS) {
            return;
        }

        const generation = resolved.generation;
        resolved.attemptsByPayload.set(payloadKey, attempts + 1);
        resolved.sending = true;

        let delivery = {delivered: false, retryable: true, reason: 'unknown'};

        try {
            delivery = await sendAnalyticsPayload(payload, 'GA идентификаторы');
        } finally {
            resolved.sending = false;
        }

        if (generation !== resolved.generation || resolved.consentRejected) {
            void maybeSendIdentifiers();
            return;
        }

        if (delivery.reason === 'consent_rejected') {
            handleConsentRejected();
            return;
        }

        if (delivery.delivered) {
            markSent(payload);
            resolved.attemptsByPayload.delete(payloadKey);

            if (JSON.stringify(buildPayload()) !== payloadKey) {
                void maybeSendIdentifiers();
            }

            return;
        }

        if (JSON.stringify(buildPayload()) !== payloadKey) {
            void maybeSendIdentifiers();
            return;
        }

        if (!delivery.retryable) {
            resolved.terminalPayloads.add(payloadKey);
            console.error('GA идентификаторы отклонены без повтора');
            return;
        }

        if ((resolved.attemptsByPayload.get(payloadKey) || 0) >= MAX_SEND_ATTEMPTS) {
            console.error('GA идентификаторы не доставлены, повторы исчерпаны');
            return;
        }

        scheduleRetry();
    };

    const requestTransport = () => {
        resolved.transportRequested = true;
        void maybeSendIdentifiers();
    };

    const scheduleFallback = () => {
        clearFallbackTimer();
        resolved.fallbackTimer = setTimeout(() => {
            resolved.fallbackTimer = null;
            if (resolved.consentRejected) {
                return;
            }

            resolved.fallbackFired = true;
            void maybeSendIdentifiers();
        }, 2000);
    };

    function requestGtagValues() {
        const generation = resolved.generation;

        gtag('get', measurementId, 'client_id', (clientId) => {
            if (generation !== resolved.generation || resolved.consentRejected) {
                return;
            }

            resolved.clientReady = true;

            if (!clientId) {
                console.warn('Не удалось получить client_id');
                void maybeSendIdentifiers();
                return;
            }

            storeClientId(clientId);
            void maybeSendIdentifiers();
        });

        gtag('get', measurementId, 'session_id', (sessionId) => {
            if (generation !== resolved.generation || resolved.consentRejected) {
                return;
            }

            resolved.sessionReady = true;

            if (!hasValue(sessionId)) {
                console.warn('Не удалось получить session_id');
                void maybeSendIdentifiers();
                return;
            }

            storeSessionId(sessionId);
            void maybeSendIdentifiers();
        });
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('ga-transport-requested', requestTransport, {once: true});
        window.addEventListener('cookie-rejected', handleConsentRejected);
        window.addEventListener('cookie-accepted', handleConsentAccepted);
        window.__vsebankiRequestGaTransport = requestTransport;
    }

    if (typeof window !== 'undefined' && window.location?.hash === '#modal') {
        requestTransport();
    }

    if (document.querySelector('#loan-verify-page-form')) {
        requestTransport();
    }

    if (resolved.consentRejected) {
        clearStoredIdentifiers();
    }

    scheduleFallback();
    if (!resolved.consentRejected) {
        requestGtagValues();
    }
}
